<template>
  <div :class="['input-container', { 'is-safari': isSafari, 'is-mobile': isMobile }]">
    <input ref="inputRef" :id="id" type="time" :value="modelValue" :min="min" :max="max" @input="updateTime" />
  </div>
</template>

<script setup lang="ts">
import { isMobile, isSafari } from '@/core/helpers/ui.helper';
import { onBeforeUnmount, ref } from 'vue';

interface TimeProps {
  modelValue: string;
  id?: string;
  min?: string;
  max?: string;
  isRoundedTo30Min?: boolean;
}

const props = defineProps<TimeProps>();

const inputRef = ref<HTMLInputElement>();
const ROUNDING_DEBOUNCE_MS = 600;
let roundingTimeout: ReturnType<typeof setTimeout> | null = null;

const emit = defineEmits(['update:modelValue']);

const parseTimeToMinutes = (time: string): number | null => {
  const parts = time.split(':');
  if (parts.length < 2) {
    return null;
  }

  const hours = Number(parts[0]);
  const minutes = Number(parts[1]);
  if (!Number.isInteger(hours) || !Number.isInteger(minutes)) {
    return null;
  }

  return hours * 60 + minutes;
};

const formatMinutesToTime = (totalMinutes: number): string => {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

const roundToNearest30Minutes = (time: string): string | null => {
  const totalMinutes = parseTimeToMinutes(time);
  if (totalMinutes === null) {
    return null;
  }

  const rounded = Math.min(Math.floor((totalMinutes + 15) / 30) * 30, 23 * 60 + 30);
  return formatMinutesToTime(rounded);
};

const clampTime = (time: string, min?: string, max?: string): string => {
  const timeMinutes = parseTimeToMinutes(time);
  if (timeMinutes === null) {
    return time;
  }

  const minMinutes = min ? parseTimeToMinutes(min) : null;
  const maxMinutes = max ? parseTimeToMinutes(max) : null;

  if (minMinutes !== null && timeMinutes < minMinutes) {
    return formatMinutesToTime(minMinutes);
  }

  if (maxMinutes !== null && timeMinutes > maxMinutes) {
    return formatMinutesToTime(maxMinutes);
  }

  return time;
};

const clearRoundingTimeout = () => {
  if (roundingTimeout) {
    clearTimeout(roundingTimeout);
    roundingTimeout = null;
  }
};

const emitRoundedTime = () => {
  const rawValue = inputRef.value?.value || props.modelValue;
  const roundedValue = roundToNearest30Minutes(rawValue);
  const normalizedValue = clampTime(roundedValue ?? rawValue, props.min, props.max);

  if (inputRef.value && inputRef.value.value !== normalizedValue) {
    inputRef.value.value = normalizedValue;
  }

  emit('update:modelValue', normalizedValue);
};

const updateTime = () => {
  if (!props.isRoundedTo30Min) {
    emit('update:modelValue', inputRef.value?.value || props.modelValue);
    return;
  }

  clearRoundingTimeout();
  roundingTimeout = setTimeout(() => {
    emitRoundedTime();
    roundingTimeout = null;
  }, ROUNDING_DEBOUNCE_MS);
};

onBeforeUnmount(() => {
  clearRoundingTimeout();
});
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;
@use '@/ui/css/partial';

.is-mobile input::-webkit-calendar-picker-indicator {
  display: none;
}

input {
  text-align: center;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  font-size: 16px;
  line-height: 45px;
  height: 45px;
  padding: 0;
  margin: 0;
  font-family:
    'Hanken Grotesk',
    system-ui,
    -apple-system,
    sans-serif;
  padding-left: 18px;
  padding-right: 18px;
  -webkit-user-select: none;
  user-select: none;
  width: 100%;
  color: var(--color-white-100);
}

.input-container {
  @extend %gam-special-border;
  border: 1px solid var(--color-white-20);
  border-radius: 25px;
  height: 47px;
  position: relative;
  display: flex;
  align-items: stretch;
  justify-content: stretch;

  &:before {
    background: var(--color-linear-gradient);
    opacity: 0;
    inset: -1px;
  }

  @include hover {
    &:before {
      opacity: 1;
    }
  }

  &.is-safari input {
    text-align: center;
    line-height: 42px;
    padding-left: 22px;
    padding-right: 22px;
    // font-weight: 400;
  }
}
</style>
